import { useCallback, useState } from 'react';
import { env } from 'Shared/constants/env';
import SmartyStreetsSDK from 'smartystreets-javascript-sdk';

const SmartyStreetsCore = SmartyStreetsSDK.core;
const credentials = new SmartyStreetsCore.SharedCredentials(
  env.smartyStreetsId
);
const client = SmartyStreetsCore.buildClient.usAutocompletePro(credentials);
const AddressLookup = SmartyStreetsSDK.usAutocompletePro.Lookup;

export interface Suggestion
  extends SmartyStreetsSDK.usAutocompletePro.Suggestion {
  text: string;
}

type UseAddressLookupType = [
  {
    data: Suggestion[] | undefined;
    loading: boolean;
    error: string | undefined;
  },
  (address: string) => Promise<SmartyStreetsSDK.usAutocompletePro.Lookup>,
];

type AddressLookupConfig = {
  stateFilter?: string[];
};

export const useAddressLookup = (
  config?: AddressLookupConfig
): UseAddressLookupType => {
  const [data, setData] = useState<Suggestion[] | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);

  const getAddressSuggestions = useCallback(
    (address: string): Promise<SmartyStreetsSDK.usAutocompletePro.Lookup> => {
      const lookup = new AddressLookup(address);

      // filter results to only include certain states
      if (config?.stateFilter?.length) {
        lookup.includeOnlyStates = config.stateFilter;
      }

      setLoading(true);

      return client
        .send(lookup)
        .then(({ result }) => {
          const computedResults: Suggestion[] = result.map((suggestion) => ({
            ...suggestion,
            text: `${suggestion.streetLine}, ${suggestion.city} ${suggestion.state}`,
          }));
          setData(computedResults);
          return lookup;
        })
        .catch((err) => {
          setError('Address Invalid');
          throw err;
        })
        .finally(() => setLoading(false));
    },
    [config?.stateFilter]
  );

  return [{ data, loading, error }, getAddressSuggestions];
};
